import React, { useMemo, useState } from 'react';
import Layout from '../../../components/Layout';

/* ============================================================================
 * MOCKUP — Acquirenti capitolato: colonne Preventivi + Allegati
 *
 * Mostra tre viste navigabili tra loro:
 *   1) TabCapitolato → sub-tab Acquirenti con tabella arricchita
 *      (nuove colonne: Preventivi, Allegati)
 *   2) Modale dedicata "Allegati acquirente"
 *   3) TabPreventivi della commessa con filtro acquirente attivo (chip + colonna)
 *
 * Tutti i dati sono hardcoded. Nessuna chiamata API.
 * Decisioni applicate:
 *   - Click icona Allegati → modale dedicata (non riapre Modifica acquirente)
 *   - Filtro acquirente in TabPreventivi: chip rimovibile + colonna sempre visibile
 *   - Conteggio "Preventivi" = TUTTI gli stati senza esclusioni
 * ============================================================================ */

// ── Icone SVG ────────────────────────────────────────────────────────────────

const PaperclipIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const FileTextIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const XIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

// ── Tipi locali ─────────────────────────────────────────────────────────────

interface AcquirenteMock {
  id: number;
  unitaCodice: string;
  unitaDescrizione: string | null;
  nome: string;
  cognome: string;
  telefono: string | null;
  email: string | null;
  note: string | null;
  numAllegati: number;
  numPreventivi: number;
}

interface PreventivoMock {
  id: number;
  numero: string;
  versione: number;
  tipo: 'SEMPLICE' | 'CONFIGURATO_COMMESSA';
  oggetto: string;
  acquirenteId: number | null;
  acquirenteUnita: string | null;
  acquirenteNome: string | null;
  acquirenteCognome: string | null;
  clienteRagioneSociale: string | null;
  dataEmissione: string;
  stato: 'BOZZA' | 'INVIATO' | 'ACCETTATO' | 'CONVERTITO' | 'SUPERATO' | 'ANNULLATO' | 'RIFIUTATO';
  totaleCliente: number | null;
}

interface AllegatoAcqMock {
  id: number;
  nome: string;
  nomeFile: string;
  dimensione: number;
  mimeType: string;
  ivaAgevolata: boolean;
  uploadedAt: string;
  uploadedByNome: string;
}

// ── Dati hardcoded ──────────────────────────────────────────────────────────

const ACQUIRENTI: AcquirenteMock[] = [
  {
    id: 1, unitaCodice: 'A/01', unitaDescrizione: 'Piano terra int. 1',
    nome: 'Mario', cognome: 'Rossi',
    telefono: '+39 333 1234567', email: 'mario.rossi@example.com',
    note: 'Cliente preferenziale',
    numAllegati: 3, numPreventivi: 2,
  },
  {
    id: 2, unitaCodice: 'A/02', unitaDescrizione: 'Piano terra int. 2',
    nome: 'Lucia', cognome: 'Bianchi',
    telefono: null, email: 'lucia.bianchi@example.com',
    note: null,
    numAllegati: 1, numPreventivi: 0,
  },
  {
    id: 3, unitaCodice: 'B/01', unitaDescrizione: 'Primo piano int. 3',
    nome: 'Giovanni', cognome: 'Verdi',
    telefono: '+39 340 9876543', email: null,
    note: null,
    numAllegati: 0, numPreventivi: 1,
  },
  {
    id: 4, unitaCodice: 'B/02', unitaDescrizione: null,
    nome: 'Francesca', cognome: 'Neri',
    telefono: null, email: null,
    note: 'IVA agevolata da verificare',
    numAllegati: 2, numPreventivi: 4,
  },
];

const PREVENTIVI: PreventivoMock[] = [
  {
    id: 101, numero: 'PRE-CER-2026/0014', versione: 2, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Bagno + cucina extra capitolato',
    acquirenteId: 1, acquirenteUnita: 'A/01', acquirenteNome: 'Mario', acquirenteCognome: 'Rossi',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-12', stato: 'ACCETTATO', totaleCliente: 15420.50,
  },
  {
    id: 102, numero: 'PRE-CER-2026/0008', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Pavimenti zona giorno',
    acquirenteId: 1, acquirenteUnita: 'A/01', acquirenteNome: 'Mario', acquirenteCognome: 'Rossi',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-03-05', stato: 'SUPERATO', totaleCliente: 8200.00,
  },
  {
    id: 103, numero: 'PRE-CER-2026/0021', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Sanitari personalizzati',
    acquirenteId: 3, acquirenteUnita: 'B/01', acquirenteNome: 'Giovanni', acquirenteCognome: 'Verdi',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-22', stato: 'INVIATO', totaleCliente: 3120.00,
  },
  {
    id: 104, numero: 'PRE-CER-2026/0023', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Cucina completa',
    acquirenteId: 4, acquirenteUnita: 'B/02', acquirenteNome: 'Francesca', acquirenteCognome: 'Neri',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-25', stato: 'BOZZA', totaleCliente: 9850.75,
  },
  {
    id: 105, numero: 'PRE-CER-2026/0010', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Bagno padronale',
    acquirenteId: 4, acquirenteUnita: 'B/02', acquirenteNome: 'Francesca', acquirenteCognome: 'Neri',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-03-18', stato: 'ANNULLATO', totaleCliente: 4210.00,
  },
  {
    id: 106, numero: 'PRE-CER-2026/0019', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Bagno secondario',
    acquirenteId: 4, acquirenteUnita: 'B/02', acquirenteNome: 'Francesca', acquirenteCognome: 'Neri',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-18', stato: 'CONVERTITO', totaleCliente: 6420.30,
  },
  {
    id: 107, numero: 'PRE-CER-2026/0027', versione: 1, tipo: 'CONFIGURATO_COMMESSA',
    oggetto: 'Lavanderia',
    acquirenteId: 4, acquirenteUnita: 'B/02', acquirenteNome: 'Francesca', acquirenteCognome: 'Neri',
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-26', stato: 'BOZZA', totaleCliente: null,
  },
  {
    id: 108, numero: 'PRE-CER-2026/0030', versione: 1, tipo: 'SEMPLICE',
    oggetto: 'Modifica capitolato base',
    acquirenteId: null, acquirenteUnita: null, acquirenteNome: null, acquirenteCognome: null,
    clienteRagioneSociale: 'Edilizia Costruzioni S.r.l.',
    dataEmissione: '2026-04-26', stato: 'INVIATO', totaleCliente: 1250.00,
  },
];

const ALLEGATI_DEMO: Record<number, AllegatoAcqMock[]> = {
  1: [
    { id: 901, nome: 'Dichiarazione IVA 4%', nomeFile: 'iva_4_rossi.pdf',
      dimensione: 245680, mimeType: 'application/pdf',
      ivaAgevolata: true, uploadedAt: '2026-03-04T09:23:00Z', uploadedByNome: 'Paolo' },
    { id: 902, nome: 'Carta identità', nomeFile: 'CI_rossi.jpg',
      dimensione: 1240500, mimeType: 'image/jpeg',
      ivaAgevolata: false, uploadedAt: '2026-03-04T09:24:00Z', uploadedByNome: 'Paolo' },
    { id: 903, nome: 'Visura immobile', nomeFile: 'visura_A01.pdf',
      dimensione: 387200, mimeType: 'application/pdf',
      ivaAgevolata: false, uploadedAt: '2026-04-10T15:11:00Z', uploadedByNome: 'Sara' },
  ],
  2: [
    { id: 910, nome: 'Dichiarazione IVA 4%', nomeFile: 'iva_4_bianchi.pdf',
      dimensione: 220100, mimeType: 'application/pdf',
      ivaAgevolata: true, uploadedAt: '2026-03-12T11:00:00Z', uploadedByNome: 'Paolo' },
  ],
  3: [],
  4: [
    { id: 920, nome: 'Dichiarazione IVA 4%', nomeFile: 'iva_4_neri.pdf',
      dimensione: 198400, mimeType: 'application/pdf',
      ivaAgevolata: true, uploadedAt: '2026-04-01T08:30:00Z', uploadedByNome: 'Sara' },
    { id: 921, nome: 'Comodato uso parente', nomeFile: 'comodato_neri.pdf',
      dimensione: 510000, mimeType: 'application/pdf',
      ivaAgevolata: false, uploadedAt: '2026-04-15T16:42:00Z', uploadedByNome: 'Paolo' },
  ],
};

const STATO_LABEL: Record<string, string> = {
  BOZZA: 'Bozza', INVIATO: 'Inviato', ACCETTATO: 'Accettato',
  RIFIUTATO: 'Rifiutato', SCADUTO: 'Scaduto', CONVERTITO: 'Convertito',
  ANNULLATO: 'Annullato', SUPERATO: 'Superato',
};

const STATO_BADGE_CLS: Record<string, string> = {
  BOZZA:      'bop-badge--neutral',
  INVIATO:    'bop-badge--info',
  ACCETTATO:  'bop-badge--success',
  RIFIUTATO:  'bop-badge--danger',
  SCADUTO:    'bop-badge--warning',
  CONVERTITO: 'bop-badge--success',
  ANNULLATO:  'bop-badge--neutral',
  SUPERATO:   'bop-badge--neutral',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT');
}

// ────────────────────────────────────────────────────────────────────────────
// Componente principale
// ────────────────────────────────────────────────────────────────────────────

export default function MockupAcquirentiPreventiviAllegati() {
  // Tab esterno della scheda commessa (simulato)
  // 0 = Capitolato (mostriamo il sotto-tab Acquirenti), 1 = Preventivi
  const [tabCommessa, setTabCommessa] = useState<0 | 1>(0);

  // Filtro acquirente attivo nel tab Preventivi
  const [filtroAcq, setFiltroAcq] = useState<{ id: number; label: string } | null>(null);

  // Modale allegati dedicata
  const [modalAllegatiAcqId, setModalAllegatiAcqId] = useState<number | null>(null);

  const apriPreventiviPerAcquirente = (a: AcquirenteMock) => {
    setFiltroAcq({
      id: a.id,
      label: `${a.unitaCodice} — ${a.nome} ${a.cognome}`,
    });
    setTabCommessa(1);
  };

  return (
    <Layout>
      <style>{`
        /* Stili locali del mockup. In produzione viveranno in bop-design-system.css */
        .mock-disclaimer {
          background: #fef9c3; border: 1px solid #fde047; color: #713f12;
          border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; font-size: 13px;
        }
        .mock-tab-cmd-bar {
          display: flex; gap: 8px; background: #f8fafc;
          border: 1px solid #e2e8f0; border-radius: 8px;
          padding: 6px; margin-bottom: 16px;
        }
        .mock-tab-cmd {
          padding: 8px 14px; border: none; border-radius: 6px;
          background: transparent; cursor: pointer; font-size: 14px; color: #475569;
        }
        .mock-tab-cmd--active { background: white; color: #1e293b; box-shadow: 0 1px 2px rgba(0,0,0,.06); font-weight: 600; }

        /* Cella Preventivi: bottone-link con badge contatore */
        .acq-prev-cell {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border: 1px solid #cbd5e1; border-radius: 6px;
          background: #f8fafc; cursor: pointer; font-size: 13px; color: #1e293b;
        }
        .acq-prev-cell:hover { background: #e2e8f0; border-color: #94a3b8; }
        .acq-prev-cell--zero { color: #94a3b8; cursor: default; background: transparent; border-color: transparent; }
        .acq-prev-cell--zero:hover { background: transparent; border-color: transparent; }

        /* Cella Allegati: icona graffetta + numero */
        .acq-alleg-cell {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border: 1px solid #cbd5e1; border-radius: 6px;
          background: #f8fafc; cursor: pointer; font-size: 13px; color: #1e293b;
        }
        .acq-alleg-cell:hover { background: #e2e8f0; border-color: #94a3b8; }
        .acq-alleg-cell--zero { color: #94a3b8; cursor: default; background: transparent; border-color: transparent; }
        .acq-alleg-cell--zero:hover { background: transparent; border-color: transparent; }

        /* Chip filtro attivo */
        .filtro-chip {
          display: inline-flex; align-items: center; gap: 8px;
          background: #dbeafe; border: 1px solid #93c5fd; color: #1e40af;
          padding: 6px 10px 6px 12px; border-radius: 999px; font-size: 13px;
          font-weight: 500;
        }
        .filtro-chip__close {
          background: transparent; border: none; color: #1e40af; cursor: pointer;
          display: inline-flex; align-items: center; padding: 2px;
          border-radius: 50%;
        }
        .filtro-chip__close:hover { background: rgba(30,64,175,.12); }

        .filtro-bar {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
        }

        /* Modale dedicata allegati: layout */
        .alleg-modal-banner {
          background: #f1f5f9; padding: 10px 14px; border-radius: 6px;
          margin-bottom: 16px; font-size: 13px; color: #475569;
        }
        .alleg-modal-row {
          display: grid; grid-template-columns: 2fr auto auto auto auto;
          gap: 12px; align-items: center;
          padding: 10px 12px; border-bottom: 1px solid #e2e8f0;
        }
        .alleg-modal-row:hover { background: #f8fafc; }
        .alleg-modal-row__nome {
          font-weight: 500; color: #1e293b; font-size: 14px;
        }
        .alleg-modal-row__file {
          font-size: 12px; color: #64748b; margin-top: 2px;
        }
        .alleg-modal-row__iva {
          display: inline-flex; align-items: center; gap: 6px; font-size: 12px;
        }
        .alleg-modal-empty {
          padding: 32px 16px; text-align: center; color: #94a3b8;
          background: #f8fafc; border-radius: 6px;
        }
        .alleg-upload-card {
          margin-top: 16px; padding: 14px;
          border: 1px dashed #cbd5e1; border-radius: 8px;
          background: #f8fafc;
        }
        .alleg-upload-row {
          display: grid; grid-template-columns: 1fr 1fr auto auto;
          gap: 10px; align-items: end; margin-bottom: 8px;
        }
      `}</style>

      <div style={{ padding: '24px' }}>
        <div className="mock-disclaimer">
          <strong>Mockup di sviluppo</strong> — Acquirenti capitolato: colonne Preventivi + Allegati.
          Tutti i dati sono finti. Nessuna chiamata API. Usa le tab qui sotto per simulare la
          navigazione tra i tab interni di una scheda commessa.
        </div>

        <h1 className="bop-page-title" style={{ marginBottom: '6px' }}>
          Commessa CER-2026/014 — Edilizia Costruzioni S.r.l.
        </h1>
        <div className="bop-text-muted" style={{ marginBottom: '20px' }}>
          Anteprima delle modifiche al sotto-tab Acquirenti del tab Capitolato e
          al tab Preventivi della scheda commessa.
        </div>

        {/* Simulazione barra dei tab della scheda commessa */}
        <div className="mock-tab-cmd-bar">
          <button
            className={`mock-tab-cmd ${tabCommessa === 0 ? 'mock-tab-cmd--active' : ''}`}
            onClick={() => setTabCommessa(0)}
          >
            Capitolato › Acquirenti
          </button>
          <button
            className={`mock-tab-cmd ${tabCommessa === 1 ? 'mock-tab-cmd--active' : ''}`}
            onClick={() => setTabCommessa(1)}
          >
            Preventivi
          </button>
        </div>

        {tabCommessa === 0 && (
          <SubTabAcquirenti
            acquirenti={ACQUIRENTI}
            onApriPreventivi={apriPreventiviPerAcquirente}
            onApriAllegati={(id) => setModalAllegatiAcqId(id)}
          />
        )}

        {tabCommessa === 1 && (
          <TabPreventiviCommessa
            preventivi={PREVENTIVI}
            filtroAcq={filtroAcq}
            onResetFiltro={() => setFiltroAcq(null)}
          />
        )}

        {modalAllegatiAcqId != null && (
          <ModaleAllegatiAcquirente
            acquirente={ACQUIRENTI.find(a => a.id === modalAllegatiAcqId)!}
            allegati={ALLEGATI_DEMO[modalAllegatiAcqId] || []}
            onClose={() => setModalAllegatiAcqId(null)}
          />
        )}
      </div>
    </Layout>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sotto-tab Acquirenti (con nuove colonne)
// ────────────────────────────────────────────────────────────────────────────

function SubTabAcquirenti({
  acquirenti,
  onApriPreventivi,
  onApriAllegati,
}: {
  acquirenti: AcquirenteMock[];
  onApriPreventivi: (a: AcquirenteMock) => void;
  onApriAllegati: (id: number) => void;
}) {
  return (
    <>
      <div style={{
        background: '#eff6ff', border: '1px solid #bfdbfe',
        color: '#1e40af', padding: '10px 14px', borderRadius: '6px',
        fontSize: '13px', marginBottom: '16px',
      }}>
        Gli acquirenti sono i clienti finali delle unità abitative. I preventivi
        degli extra-capitolato possono essere intestati direttamente a loro.
        Cliccando sul numero in colonna <strong>Preventivi</strong> apri il tab
        Preventivi filtrato per quell'acquirente. Cliccando sull'icona in colonna
        <strong> Allegati</strong> apri la modale di gestione allegati.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span>{acquirenti.length} acquirenti registrati</span>
        <button className="bop-btn bop-btn-primary">+ Aggiungi acquirente</button>
      </div>

      <div className="bop-card">
        <table className="bop-table">
          <thead>
            <tr>
              <th>Unità</th>
              <th>Acquirente</th>
              <th>Telefono</th>
              <th>Email</th>
              <th style={{ textAlign: 'center' }}>Preventivi</th>
              <th style={{ textAlign: 'center' }}>Allegati</th>
              <th>Note</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {acquirenti.map(a => (
              <tr key={a.id}>
                <td>
                  <strong>{a.unitaCodice}</strong>
                  {a.unitaDescrizione && (
                    <><br /><span className="bop-text-muted">{a.unitaDescrizione}</span></>
                  )}
                </td>
                <td>{a.nome} {a.cognome}</td>
                <td>{a.telefono || <span className="bop-text-muted">—</span>}</td>
                <td>{a.email || <span className="bop-text-muted">—</span>}</td>

                {/* COLONNA NUOVA: Preventivi */}
                <td style={{ textAlign: 'center' }}>
                  {a.numPreventivi === 0 ? (
                    <span className="acq-prev-cell acq-prev-cell--zero">—</span>
                  ) : (
                    <button
                      className="acq-prev-cell"
                      onClick={() => onApriPreventivi(a)}
                      title={`Vai ai ${a.numPreventivi} preventivi di ${a.nome} ${a.cognome}`}
                    >
                      <FileTextIcon size={14} />
                      <span>{a.numPreventivi}</span>
                    </button>
                  )}
                </td>

                {/* COLONNA NUOVA: Allegati */}
                <td style={{ textAlign: 'center' }}>
                  {a.numAllegati === 0 ? (
                    <span className="acq-alleg-cell acq-alleg-cell--zero" title="Nessun allegato">
                      <PaperclipIcon size={14} />
                      <span>0</span>
                    </span>
                  ) : (
                    <button
                      className="acq-alleg-cell"
                      onClick={() => onApriAllegati(a.id)}
                      title={`Gestisci ${a.numAllegati} allegati`}
                    >
                      <PaperclipIcon size={14} />
                      <span>{a.numAllegati}</span>
                    </button>
                  )}
                </td>

                <td><span className="bop-text-muted">{a.note || '—'}</span></td>

                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="bop-btn bop-btn-secondary" style={{ padding: '4px 10px' }}>Modifica</button>{' '}
                  <button className="bop-btn bop-btn-danger" style={{ padding: '4px 10px' }}>Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tab Preventivi della commessa (con filtro acquirente + colonna Acquirente)
// ────────────────────────────────────────────────────────────────────────────

function TabPreventiviCommessa({
  preventivi,
  filtroAcq,
  onResetFiltro,
}: {
  preventivi: PreventivoMock[];
  filtroAcq: { id: number; label: string } | null;
  onResetFiltro: () => void;
}) {
  const visibili = useMemo(() => {
    if (!filtroAcq) return preventivi;
    return preventivi.filter(p => p.acquirenteId === filtroAcq.id);
  }, [preventivi, filtroAcq]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h2 className="bop-page-title" style={{ margin: 0 }}>Preventivi della commessa</h2>
        <button className="bop-btn bop-btn-primary">+ Nuovo preventivo</button>
      </div>

      {/* Barra filtri: chip rimovibile quando filtro attivo */}
      <div className="filtro-bar">
        {filtroAcq ? (
          <div className="filtro-chip">
            <FilterIcon />
            <span>Filtro acquirente: <strong>{filtroAcq.label}</strong></span>
            <button
              className="filtro-chip__close"
              onClick={onResetFiltro}
              aria-label="Rimuovi filtro acquirente"
              title="Rimuovi filtro"
            >
              <XIcon size={14} />
            </button>
          </div>
        ) : (
          <span className="bop-text-muted" style={{ fontSize: 13 }}>
            Nessun filtro attivo · {preventivi.length} preventivi totali in commessa
          </span>
        )}
      </div>

      {visibili.length === 0 ? (
        <div className="bop-empty-state">
          {filtroAcq
            ? `Nessun preventivo per ${filtroAcq.label}.`
            : 'Nessun preventivo collegato a questa commessa.'}
        </div>
      ) : (
        <div className="bop-card">
          <table className="bop-table">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Tipo</th>
                <th>Oggetto</th>
                <th>Acquirente</th>
                <th>Cliente</th>
                <th>Data</th>
                <th>Stato</th>
                <th style={{ textAlign: 'right' }}>Totale</th>
              </tr>
            </thead>
            <tbody>
              {visibili.map(p => (
                <tr key={p.id} style={{ cursor: 'pointer' }}>
                  <td>{p.numero}{p.versione > 1 ? ` (v${p.versione})` : ''}</td>
                  <td>{p.tipo === 'SEMPLICE' ? 'Semplice' : 'Configurato'}</td>
                  <td>{p.oggetto}</td>
                  <td>
                    {p.acquirenteId ? (
                      <span>
                        <strong>{p.acquirenteUnita}</strong>
                        <br />
                        <span className="bop-text-muted">{p.acquirenteNome} {p.acquirenteCognome}</span>
                      </span>
                    ) : (
                      <span className="bop-text-muted">—</span>
                    )}
                  </td>
                  <td>{p.clienteRagioneSociale || '—'}</td>
                  <td>{formatDate(p.dataEmissione)}</td>
                  <td>
                    <span className={`bop-badge ${STATO_BADGE_CLS[p.stato] || 'bop-badge--neutral'}`}>
                      {STATO_LABEL[p.stato] || p.stato}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {p.totaleCliente != null ? `€ ${formatCurrency(p.totaleCliente)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modale dedicata: Allegati acquirente
// ────────────────────────────────────────────────────────────────────────────

function ModaleAllegatiAcquirente({
  acquirente,
  allegati,
  onClose,
}: {
  acquirente: AcquirenteMock;
  allegati: AllegatoAcqMock[];
  onClose: () => void;
}) {
  const [list, setList] = useState<AllegatoAcqMock[]>(allegati);
  const [nomeNuovo, setNomeNuovo] = useState('');
  const [fileSelected, setFileSelected] = useState<string>(''); // solo nome file simulato
  const [ivaAgevolataNuovo, setIvaAgevolataNuovo] = useState(false);

  const eliminaSimulato = (id: number) => {
    if (!window.confirm('Eliminare l\'allegato? L\'azione non è reversibile.')) return;
    setList(prev => prev.filter(a => a.id !== id));
  };

  const aggiungiSimulato = () => {
    if (!fileSelected || !nomeNuovo.trim()) return;
    const newId = Math.max(0, ...list.map(a => a.id)) + 1;
    setList(prev => [{
      id: newId,
      nome: nomeNuovo.trim(),
      nomeFile: fileSelected,
      dimensione: 250000,
      mimeType: 'application/pdf',
      ivaAgevolata: ivaAgevolataNuovo,
      uploadedAt: new Date().toISOString(),
      uploadedByNome: 'Tu',
    }, ...prev]);
    setNomeNuovo('');
    setFileSelected('');
    setIvaAgevolataNuovo(false);
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal" style={{ maxWidth: 720, width: '95vw' }}>
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">
            Allegati acquirente · {acquirente.unitaCodice} — {acquirente.nome} {acquirente.cognome}
          </h3>
          <button className="bop-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="bop-modal__body">
          <div className="alleg-modal-banner">
            Dichiarazioni IVA agevolata, copie di documenti e altri allegati di questo acquirente.
            Gli stessi allegati sono visibili anche aprendo un preventivo intestato a questo acquirente.
          </div>

          {/* Lista allegati */}
          {list.length === 0 ? (
            <div className="alleg-modal-empty">Nessun allegato presente.</div>
          ) : (
            <div>
              {list.map(a => (
                <div key={a.id} className="alleg-modal-row">
                  <div>
                    <div className="alleg-modal-row__nome">{a.nome}</div>
                    <div className="alleg-modal-row__file">
                      {a.nomeFile} · {formatBytes(a.dimensione)} · caricato il {formatDate(a.uploadedAt)} da {a.uploadedByNome}
                    </div>
                  </div>
                  <label className="alleg-modal-row__iva">
                    <input
                      type="checkbox"
                      defaultChecked={a.ivaAgevolata}
                      onChange={() => { /* in produzione: PUT /api/acquirenti/:id/allegati/:id */ }}
                    />
                    <span>IVA 4%</span>
                  </label>
                  <button className="bop-btn bop-btn-secondary" style={{ padding: '4px 10px' }} title="Scarica">
                    <DownloadIcon /> Scarica
                  </button>
                  <button className="bop-btn bop-btn-danger" style={{ padding: '4px 10px' }} onClick={() => eliminaSimulato(a.id)} title="Elimina">
                    <TrashIcon />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Card upload nuovo allegato */}
          <div className="alleg-upload-card">
            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
              Carica nuovo allegato
            </div>
            <div className="alleg-upload-row">
              <div className="bop-form-group" style={{ margin: 0 }}>
                <label className="bop-label">File</label>
                <input
                  type="file"
                  className="bop-input"
                  onChange={e => setFileSelected(e.target.files?.[0]?.name || '')}
                />
              </div>
              <div className="bop-form-group" style={{ margin: 0 }}>
                <label className="bop-label">Nome allegato</label>
                <input
                  className="bop-input"
                  value={nomeNuovo}
                  onChange={e => setNomeNuovo(e.target.value)}
                  placeholder="es. Dichiarazione IVA 4%"
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={ivaAgevolataNuovo}
                  onChange={e => setIvaAgevolataNuovo(e.target.checked)}
                />
                <span>IVA 4%</span>
              </label>
              <button
                className="bop-btn bop-btn-primary"
                disabled={!fileSelected || !nomeNuovo.trim()}
                onClick={aggiungiSimulato}
                style={{ marginBottom: 4 }}
              >
                <UploadIcon /> Carica
              </button>
            </div>
            <div className="bop-text-muted" style={{ fontSize: 12 }}>
              Formati accettati: PDF, JPG, PNG, GIF, WebP, DOC/DOCX, XLS/XLSX. Max 20 MB.
            </div>
          </div>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
